import moment from "moment";
import { useState, useEffect } from "react";
import Cookies from "universal-cookie";
import UserService from "../services/user/UserService";
import IPasswordResetRequest from "../models/requests/user/passwordResetRequest";
import { useTranslation } from "react-i18next";

import { validEmailRegex } from "../utils/regex";
import passwordUtils from "../utils/passwordUtils";

const COOKIE_EXPIRATION_MINUTES = 5;

const usePasswordReset = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [code, setCode] = useState("");
    const [isAlert, setIsAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [isEmptyField, setIsEmptyField] = useState(true);
    const [isValidEmail, setIsValidEmail] = useState(true);

    const cookies = new Cookies();
    const { t } = useTranslation();

    useEffect(() => {
        setIsEmptyField((email === "") || (code === "") || (password === "") || (confirmPassword == ""));
        setIsValidEmail(!validEmailRegex.test(email));
    }, [email, code, password, confirmPassword]);


    const onEmailSubmit = async () => {
        const isEmailValid = await UserService.PostResetPasswordEmail(email);

        if ((!isEmailValid) && (isEmptyField)) {
            setIsAlert(true);
            setAlertMessage(t("passwordReset.errorEmptyEmail"));
            throw new Error("invalid email");
        }

        else if ((!isEmailValid) && (isValidEmail)) {
            setIsAlert(true);
            setAlertMessage(t("login.errorInvalidEmail"));
            throw new Error("invalid email");

        }

        const isUserMicrosoftAccount = await UserService.IsUserMicrosoftAccount(email);
        if (isUserMicrosoftAccount) {
            setIsAlert(true);
            setAlertMessage(t("passwordReset.errorMicrosoftAccount"));
            throw new Error("microsoft account");
        }

        const expirationDate = moment()
            .add(COOKIE_EXPIRATION_MINUTES, "m")
            .toDate();
        cookies.set("reset_email", email, {
            expires: expirationDate,
            path: "/",
        });
    };

    const onCodeSubmit = async () => {
        const isCodeValid = await UserService.PostResetPasswordCode(
            email,
            code
        );
        if (!isCodeValid) {
            setIsAlert(true);
            setIsEmptyField(code === "");
            if (isEmptyField) {
                setAlertMessage(t("passwordReset.errorEmptyCode"));
            }
            else {
                setAlertMessage(t("passwordReset.errorCode"));
            }
            throw new Error("invalid code");
        }

        const expirationDate = moment()
            .add(COOKIE_EXPIRATION_MINUTES, "m")
            .toDate();
        cookies.set("reset_code", code, { expires: expirationDate, path: "/" });
    };

    const onPasswordSubmit = async () => {
        const user: IPasswordResetRequest = {
            code: String(code),
            email,
            confirmNewPassword: confirmPassword,
            newPassword: password
        }

        const isPasswordSet = await UserService.PostResetPassword(user);
        if ((isPasswordSet) && (!passwordUtils.isPasswordValid(password, confirmPassword))) {
            setIsAlert(true);
            setAlertMessage(t("passwordReset.errorPassword"));
            throw new Error("invalid password");
        }
        else if ((isPasswordSet) && (isEmptyField)) {
            setIsAlert(true);
            setAlertMessage(t("passwordReset.errorEmptyPassword"));
            throw new Error("invalid password");
        }
        else if (!isPasswordSet) {
            setIsAlert(true);
            if (isEmptyField) {
                setAlertMessage(t("passwordReset.errorEmptyPassword"));
            }
            else if (!passwordUtils.isPasswordValid(password, confirmPassword)) {
                setAlertMessage(t("passwordReset.errorPassword"));
            }
            throw new Error("invalid code");
        }

        cookies.remove("reset_email");
        cookies.remove("reset_code");
    };


    return {
        email,
        setEmail,
        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        code,
        setCode,
        onEmailSubmit,
        onCodeSubmit,
        onPasswordSubmit,
        isAlert,
        setIsAlert,
        alertMessage,
        setAlertMessage,
    };
};

export default usePasswordReset;